import React , { Fragment } from 'react'
import { makeStyles } from '@mui/styles'
import { styled, Theme, useTheme } from '@mui/material/styles'
import { Typography, Dialog, DialogTitle, DialogContent, Button, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/CancelOutlined'
// import ErrorIcon from '@mui/icons-material/DangerousRounded'
const IconErrorImage = `${process.env.REACT_APP_IMAGE_PATH}/assets/image/icon_error.png`

type SeverityType = 'error' | 'info'

type DialogProps = {
    onClose?: () => void
    onClickConfirm?: () => void
    onClickCancel?: () => void
    onClickOk?: () => void
    children?: React.ReactNode
    isOpen: boolean
    title?: string
    content?: string
    labelButtonConfirm?: string
    labelButtonCancel?: string
    labelButtonOk?: string
    fullScreen?: boolean
    hideBackdrop?: boolean
    hideCloseButton?: boolean
    disableClickBackdrop?: boolean
    overrideClass?: {}
    type?: 'confirm' | 'ok'
    severity?: SeverityType
}

const DialogIcon = (severity: SeverityType) => {
    switch (severity) {
        case 'error':
            return <StyledIconImg src={IconErrorImage} />
        // return <IconError />
        default:
            return <></>
    }
}

const DialogControl = (props: DialogProps) => {
    const {
        onClose,
        onClickConfirm,
        onClickCancel,
        onClickOk,
        title = '',
        content = '',
        labelButtonConfirm = 'Confirm',
        labelButtonCancel = 'Cancel',
        labelButtonOk = 'Ok',
        children,
        isOpen,
        fullScreen = false,
        hideBackdrop = false,
        hideCloseButton = false,
        disableClickBackdrop = false,
        overrideClass,
        type,
        severity = 'info',
    } = props
    const theme = useTheme()
    const classes = useStyles({ severity })

    return (
        <Dialog
            open={isOpen}
            onClose={(event, reason) => {
                if (!(disableClickBackdrop && reason === 'backdropClick')) {
                    if (type === 'ok') {
                        onClickOk?.()
                    } else {
                        onClose?.()
                    }
                }
            }}
            hideBackdrop={hideBackdrop}
            fullScreen={fullScreen}
            classes={overrideClass}
        >
            <DialogContent classes={{ root: classes.root }}>
                {title && (
                    <DialogTitle sx={{ m: 0, p: 0, paddingBottom: 1 }} className={classes.title}>
                        <Typography style={{ fontWeight: 'bold' }}>{title}</Typography>
                        {!hideCloseButton && (
                            <IconButton
                                aria-label="close"
                                onClick={() => {
                                    type === 'ok' ? onClickOk?.() : onClose?.()
                                }}
                                sx={{
                                    position: 'absolute',
                                    right: 8,
                                    top: 14,
                                    color: (theme) => theme.palette.grey[500],
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        )}
                    </DialogTitle>
                )}

                <StyledDivContent>
                    {children ? (
                        children
                    ) : (
                        <Fragment>
                            {severity !== 'info' && <StyledDivContentIcon>{DialogIcon(severity)}</StyledDivContentIcon>}
                            <Typography gutterBottom className={classes.content}>
                                {content}
                            </Typography>
                        </Fragment>
                    )}
                </StyledDivContent>
                {type === 'confirm' && (
                    <StyledDivButtonGroup>
                        <StyledDivButton>
                            <Button
                                variant="outlined"
                                color={severity === 'error' ? 'error' : 'primary'}
                                onClick={onClickCancel}
                                classes={{ root: classes.buttonCancel }}
                            >
                                {labelButtonCancel}
                            </Button>
                        </StyledDivButton>
                        <StyledDivButton>
                            <Button
                                variant="contained"
                                onClick={onClickConfirm}
                                classes={{ root: classes.buttonConfirm }}
                            >
                                {labelButtonConfirm}
                            </Button>
                        </StyledDivButton>
                    </StyledDivButtonGroup>
                )}
                {type === 'ok' && (
                    <StyledDivButtonGroup>
                        <Button variant="contained" onClick={onClickOk} classes={{ root: classes.buttonConfirm }}>
                            {labelButtonOk}
                        </Button>
                    </StyledDivButtonGroup>
                )}
            </DialogContent>
        </Dialog>
    )
}

export default DialogControl

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: `0px !important`,
        minWidth: `100px !important`,
    },
    title: {
        backgroundColor: (props: any) =>
            `${
                props.severity === 'error' ? theme.palette.getContrastText('red') : theme.palette.background.default
            } !important`,
        borderBottom: (props: any) =>
            `1px solid ${
                props.severity === 'error' ? theme.palette.getContrastText('fail') : theme.palette.primary.light
            } !important`,
        color: (props: any) =>
            `${
                props.severity === 'error' ? theme.palette.getContrastText('fail') : theme.palette.primary.dark
            } !important`,
        padding: `20px 20px 10px 20px !important`,
    },
    content: {
        textAlign: `center`,
        [theme.breakpoints.up('sm')]: {
            minWidth: `250px !important`,
        },
    },
    buttonConfirm: {
        width: '120px',
        minHeight: '37px',
        backgroundColor: (props: any) =>
            `${
                props.severity === 'error' ? theme.palette.getContrastText('fail') : theme.palette.primary.dark
            } !important`,
    },
    buttonCancel: {
        width: '120px',
        minHeight: '37px',
        color: (props: any) =>
            `${
                props.severity === 'error' ? theme.palette.getContrastText('fail') : theme.palette.primary.dark
            } !important`,
        borderColor: (props: any) =>
            `${
                props.severity === 'error' ? theme.palette.getContrastText('fail') : theme.palette.primary.dark
            } !important`,
    },
}))

const StyledDivContent = styled('div')(({ theme }) => ({
    padding: `10px 20px 10px 20px !important`,
}))

const StyledDivContentIcon = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
}))

const StyledDivButtonGroup = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingBottom: '15px',
    [theme.breakpoints.down('sm')]: {
        padding: '0px 5px 15px 5px',
    },
}))

const StyledDivButton = styled('div')(({ theme }) => ({
    padding: '0px 20px',
    [theme.breakpoints.down('sm')]: {
        padding: '0px 2px',
    },
}))

const StyledIconImg = styled('img')(({ theme, src }) => ({
    maxHeight: '50px',
    padding: '5px 0px',
}))

// const IconError = styled(ErrorIcon)(({ theme }) => ({
//     color: theme.palette.getContrastText('fail'),
//     fontSize: '54px',
// }))
